import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Toaster } from 'react-hot-toast';
import { cn } from '../../lib/utils';
import { Link, Image, useRouter } from '../common/NextAdapters';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../ui/sheet';
import { useAuthStore } from '../../store';

const NAV_GROUPS = [
  {
    label: 'Content',
    items: [
      { to: '/admin/movies', label: 'Pending Movies', icon: 'ph:film-slate-bold' },
      { to: '/admin/episodes', label: 'Pending Episodes', icon: 'ph:play-circle-bold' },
      { to: '/admin/catalog', label: 'Catalog & Cast', icon: 'ph:stack-bold' },
      { to: '/admin/categories', label: 'Categories', icon: 'ph:folder-bold' },
      { to: '/admin/actors', label: 'Actors', icon: 'ph:identification-badge-bold' },
      { to: '/upload/movie', label: 'Upload Movie', icon: 'ph:upload-simple-bold', creatorAccessible: true },
      { to: '/upload/episode', label: 'Upload Episode', icon: 'ph:upload-simple-bold', creatorAccessible: true },
    ],
  },
  {
    label: 'Monetization',
    items: [
      { to: '/admin/plans', label: 'Subscription Plans', icon: 'ph:credit-card-bold' },
      { to: '/admin/subscriptions', label: 'Subscriptions', icon: 'ph:receipt-bold' },
      { to: '/admin/payouts', label: 'Payouts', icon: 'ph:currency-dollar-bold' },
      { to: '/admin/payouts/config', label: 'Payout Config', icon: 'ph:sliders-horizontal-bold' },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/admin/users', label: 'Users', icon: 'ph:users-bold' },
      { to: '/admin/content-creators', label: 'Content Creators', icon: 'ph:star-bold' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/config', label: 'System Config', icon: 'ph:gear-six-bold' },
      { to: '/admin/legal-pages', label: 'Legal Pages', icon: 'ph:scroll-bold' },
    ],
  },
];

const FLAT_NAV = [
  { to: '/admin', label: 'Overview', end: true },
  { to: '/creator', label: 'Dashboard', end: true },
  { to: '/creator/payouts', label: 'My Payouts' },
  ...NAV_GROUPS.flatMap((group) => group.items.map((item) => ({ ...item, group: group.label }))),
];

function usePageHeading(pathname) {
  return React.useMemo(() => {
    const match = FLAT_NAV.filter((item) => (item.end ? pathname === item.to : pathname.startsWith(item.to))).sort(
      (a, b) => b.to.length - a.to.length
    )[0];
    return { title: match?.label || 'Admin', group: match?.group };
  }, [pathname]);
}

function NavItem({ to, label, icon, end, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-white/[0.06] text-foreground font-semibold'
            : 'text-muted-foreground hover:bg-white/[0.04] hover:text-foreground'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="brand-sweep absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full" />
          )}
          <Icon icon={icon} className="size-4 shrink-0" />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const router = useRouter();
  const isAdmin = user?.Role === 'Admin';

  const userInitial = (user?.FullName || user?.Username || user?.Email || '')
    .trim()
    .charAt(0)
    .toUpperCase();

  const visibleGroups = isAdmin
    ? NAV_GROUPS
    : NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => item.creatorAccessible) })).filter(
        (group) => group.items.length > 0
      );

  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pt-6 pb-5">
        <Link href="/" className="flex items-center" aria-label="Kast home">
          <Image src="/logo-full.png" alt="Kast" width={112} height={34} className="h-7 w-auto" />
        </Link>
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Icon icon={isAdmin ? 'ph:shield-check-bold' : 'ph:video-camera-bold'} className="size-3.5 text-primary" />
          {isAdmin ? 'Admin' : 'Creator'}
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {isAdmin && <NavItem to="/admin" end label="Overview" icon="ph:squares-four-bold" onNavigate={onNavigate} />}
        {!isAdmin && (
          <div className="flex flex-col gap-0.5">
            <NavItem to="/creator" end label="Dashboard" icon="ph:squares-four-bold" onNavigate={onNavigate} />
            <NavItem to="/creator/payouts" label="My Payouts" icon="ph:currency-dollar-bold" onNavigate={onNavigate} />
          </div>
        )}
        {visibleGroups.map((group) => (
          <div key={group.label} className="mt-5 first:mt-0">
            <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {group.label}
            </p>
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <NavItem key={item.to} {...item} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 px-3 py-4">
        <div className="flex items-center gap-2.5 rounded-lg px-2 py-2">
          <Avatar className="size-8 ring-1 ring-white/15">
            <AvatarFallback className="brand-sweep text-xs font-bold text-white uppercase select-none">
              {userInitial || 'A'}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight text-foreground">
              {user?.FullName || user?.Username || 'Admin'}
            </p>
            <p className="truncate text-xs leading-tight text-muted-foreground">{user?.Email}</p>
          </div>
        </div>
        <div className="mt-2 flex flex-col gap-0.5">
          <Link
            href="/"
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
          >
            <Icon icon="ph:house-bold" className="size-4" />
            Back to Site
          </Link>
          <button
            type="button"
            onClick={() => {
              logout();
              router.push('/login');
            }}
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <Icon icon="ph:sign-out-bold" className="size-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminUserMenu() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const clearActiveProfile = useAuthStore((s) => s.clearActiveProfile);
  const router = useRouter();

  const userInitial = (user?.FullName || user?.Username || user?.Email || '')
    .trim()
    .charAt(0)
    .toUpperCase();

  const changeProfile = () => {
    clearActiveProfile();
    router.push('/whos-watching', { state: { fromSwitch: true } });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Account"
          className="rounded-full ring-offset-background transition-transform active:scale-95"
        >
          <Avatar className="size-8 ring-1 ring-white/15">
            <AvatarFallback className="brand-sweep text-xs font-bold text-white uppercase select-none">
              {userInitial || 'A'}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-[#16121f] border-white/10">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-0.5">
            <p className="truncate text-sm font-semibold leading-tight text-foreground">
              {user?.FullName || user?.Username || 'Admin'}
            </p>
            <p className="truncate text-xs leading-tight text-muted-foreground">{user?.Email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/" className="cursor-pointer flex items-center">
            <Icon icon="ph:house-bold" className="mr-2 size-4 text-muted-foreground" />
            Back to Site
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={changeProfile} className="cursor-pointer">
          <Icon icon="ph:users-three-bold" className="mr-2 size-4 text-muted-foreground" />
          Switch profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/upload/movie" className="cursor-pointer flex items-center">
            <Icon icon="ph:film-slate-bold" className="mr-2 size-4 text-muted-foreground" />
            Upload Movie
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/upload/episode" className="cursor-pointer flex items-center">
            <Icon icon="ph:play-circle-bold" className="mr-2 size-4 text-muted-foreground" />
            Upload Episode
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            logout();
            router.push('/login');
          }}
          className="cursor-pointer text-destructive"
        >
          <Icon icon="ph:sign-out-bold" className="mr-2 size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { title, group } = usePageHeading(location.pathname);

  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-white/10 bg-[#0a0714] lg:block">
        <SidebarContent />
      </aside>

      <main className="lg:pl-64">
        {/* Page header */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-white/10 bg-[#07050e]/90 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open admin menu" className="-ml-2 lg:hidden">
                <Icon icon="ph:list-bold" className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-white/10 bg-[#0a0714] p-0">
              <SheetTitle className="sr-only">Admin navigation</SheetTitle>
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="min-w-0 leading-tight">
            {group && (
              <p className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground/60">
                {group}
              </p>
            )}
            <h1 className="truncate text-sm font-semibold text-foreground sm:text-base">{title}</h1>
          </div>
          <div className="ml-auto flex items-center">
            <AdminUserMenu />
          </div>
        </header>

        <Outlet />
      </main>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#16121f',
            color: '#f3f0f8',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            fontSize: '14px',
          },
          success: { iconTheme: { primary: '#ff7a00', secondary: '#000' } },
        }}
      />
    </div>
  );
}
